import type { StepTransitionWrapperProps } from "./form-types";

import { cn } from "@homeinstead/ui";
import { STEP_TRANSITION_EASE_TAILWIND } from "./step-transition-constants";
import { STEP_TRANSITION_VARIANT_CLASSES } from "./step-transition-variant-classes";

export type LeadFormStepWrapperProps = {
  persistentHeroMinHeightClass?: string;
  isHeroMode: boolean;
  /**
   * Drives the step transition via CSS. The element type never changes —
   * swapping it on a `prefers-reduced-motion` change would remount every
   * step and discard in-progress selections.
   */
  stepTransitionWrapperProps: StepTransitionWrapperProps;
  children: React.ReactNode;
};

// overflow-hidden clips the hero image to the container boundary (matching
// NationalHero's overflow-hidden outer). Safe in hero mode because the info
// panel contains only a HeroImage — no icon that overflows the top edge.
const HERO_MODE_GRID_CLASS =
  "overflow-hidden -mx-4 w-[calc(100%_+_2rem)] sm:-mx-10 sm:w-[calc(100%_+_5rem)] lg:-mx-[72px] lg:w-[calc(100%_+_144px)] 2xl:-mx-[152px] 2xl:w-[calc(100%_+_304px)]";

export function LeadFormStepWrapper({
  persistentHeroMinHeightClass,
  isHeroMode,
  stepTransitionWrapperProps,
  children,
}: LeadFormStepWrapperProps) {
  const { isAnimated, isExiting, variant, exitDurationMs, enterDurationMs } =
    stepTransitionWrapperProps;
  const { transitionClass, exitClasses } =
    STEP_TRANSITION_VARIANT_CLASSES[variant];

  return (
    <div
      className={cn(
        // All direct children share grid-area 1/1 so they stack on top of each
        // other. The container height equals the tallest child — preventing
        // layout shift when switching between steps of different heights.
        // No overflow clipping: any overflow value other than "visible" creates
        // a new formatting context that clips the info-panel icon at the step's
        // top edge. The grid stacking does not need overflow containment —
        // inactive steps are visibility:hidden (not display:none) so they
        // contribute to grid height without spilling outside the grid area.
        //
        // grid-cols-1 fixes testimonial overflow: without an explicit column
        // track, the implicit auto track expands to fit the widest child (the
        // hero image on step 1 has minWidth:1600px). This makes all steps
        // inherit that inflated width and pushes ml-auto panels outside the
        // viewport. grid-cols-1 (minmax(0,1fr)) constrains the track to the
        // grid container's own width regardless of child content.
        "grid grid-cols-1 [&>*]:min-w-0 [&>*]:[grid-area:1/1]",
        persistentHeroMinHeightClass,
        isHeroMode ? HERO_MODE_GRID_CLASS : "w-full",
        isAnimated && transitionClass,
        // A single element carries the whole transition: the step sinks away to
        // these exit values, the step content swaps while invisible, then it
        // rises back to the (default) idle values. See
        // step-transition-variant-classes.ts for why the offsets are smaller
        // than AnimatedReveal's.
        isAnimated && isExiting && exitClasses,
      )}
      // Duration and easing are CMS-driven numbers, not values known at build
      // time — Tailwind can't generate arbitrary-value classes from runtime
      // data, so both live here as inline styles instead of `duration-[...]`
      // classes.
      style={
        isAnimated
          ? {
              transitionDuration: `${isExiting ? exitDurationMs : enterDurationMs}ms`,
              transitionTimingFunction: STEP_TRANSITION_EASE_TAILWIND,
            }
          : undefined
      }
    >
      {children}
    </div>
  );
}
